/* eslint-disable react/no-unstable-nested-components */
import { ChangeEvent, useEffect, useState, useRef } from "react";
import { Info } from "@remo-co/ui-core/src/icons/Info";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { FormTextInput } from "@remo-co/ui-core/src/components/FormTextInput";
import { Prompt, useHistory } from "react-router-dom";
import { Location } from "history";
import {
  EventEmailCustomizationType,
  EventEmailCustomizationMediaType,
  IEmailCustomization,
} from "modules/emailCustomization/types";
import { BestUploadSizes } from "modules/uploader/UploadCaption";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { selectCustomEmailSenderSettings } from "modules/companySettings/redux/selectors";
import { useSendTestEmail } from "modules/emailCustomization/hooks/useSendTestEmail";
import { removeEmptyStringFields } from "modules/eventForm/emails/utils";
import { validateURL, isSecureURL } from "helpers/urlHelper";
import { getVar } from "config";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { ImageUploaderField } from "../ImageUploaderField";
import { MediaSection } from "../MediaSection";
import { EmailPreviewDialog } from "../EmailPreviewDialog";
import { styles } from "./styles";

type IEmailCustomizationFormState = Omit<IEmailCustomization, "_id" | "event">;

interface Props {
  activeTab: EventEmailCustomizationType;
  customizations: IEmailCustomization[];
  onCustomizationChange: (customizations: IEmailCustomization[]) => void;
  onSave: () => Promise<void>;
  onReset: () => void;
  isSaving: boolean;
  eventId?: string;
}

export const EmailCustomizationForm = ({
  activeTab,
  customizations,
  onCustomizationChange,
  onSave,
  onReset,
  isSaving,
  eventId,
}: Props) => {
  const customEmailSenderSettings = useSelector(
    selectCustomEmailSenderSettings,
  );
  const { t } = useI18n(["eventForm", "event", "common"]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const { sendTestEmail, isSending } = useSendTestEmail(eventId);
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const history = useHistory();
  const dispatch = useAppDispatch();

  const formStateRef = useRef<IEmailCustomizationFormState>({
    type: activeTab,
    mediaType: EventEmailCustomizationMediaType.VIDEO,
  });
  const [formState, setFormState] = useState<IEmailCustomizationFormState>({
    type: activeTab,
    mediaType: EventEmailCustomizationMediaType.VIDEO,
  });

  const prevActiveTabRef = useRef(activeTab);

  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  useEffect(() => {
    if (isClearing) {
      setIsClearing(false);
      return;
    }

    const active = customizations.find(
      (customization) => customization.type === activeTab,
    );

    if (active) {
      const { _id, event: _event, ...rest } = active;
      setFormState(rest);
    } else {
      const newFormState: IEmailCustomizationFormState = {
        type: activeTab,
        mediaType: EventEmailCustomizationMediaType.VIDEO,
      };

      setFormState(newFormState);

      const existingTypes = customizations.map((c) => c.type);
      if (!existingTypes.includes(activeTab)) {
        onCustomizationChange([
          ...customizations,
          {
            event: "",
            ...newFormState,
          },
        ]);
      }
    }

    setUrlErrors({});

    const isTabChanged = prevActiveTabRef.current !== activeTab;
    if (isTabChanged) {
      setHasUnsavedChanges(false);
    }

    prevActiveTabRef.current = activeTab;
  }, [activeTab, customizations, onCustomizationChange, isClearing]);

  const validateURLField = (
    field: string,
    value: string,
  ): string | undefined => {
    if (value && (!validateURL(value) || !isSecureURL(value))) {
      return t("eventForm:customize.emails.url.validation.error");
    }
    return undefined;
  };

  const syncParentState = (updatedState: IEmailCustomizationFormState) => {
    const activeCustomization = customizations.find(
      (c) => c.type === activeTab,
    );
    const stateToSync = JSON.parse(JSON.stringify(updatedState));

    if (activeCustomization) {
      const updatedCustomizations = customizations.map((customization) =>
        customization.type === activeTab
          ? {
              ...customization,
              ...stateToSync,
            }
          : customization,
      );
      onCustomizationChange(updatedCustomizations);
    } else {
      const newCustomization: IEmailCustomization = {
        event: "",
        ...stateToSync,
      };
      onCustomizationChange([...customizations, newCustomization]);
    }
  };

  const updateField = (
    field: keyof IEmailCustomizationFormState,
    value: string | EventEmailCustomizationMediaType,
  ) => {
    const updatedState = {
      ...formState,
      [field]: value,
    };

    if (
      typeof value === "string" &&
      (field === "headerImageURL" ||
        field === "mediaURL" ||
        field === "mediaExtraImageURL" ||
        field === "senderImageURL")
    ) {
      const error = validateURLField(field as string, value);
      setUrlErrors((prev) => ({
        ...prev,
        [field]: error || "",
      }));
    }

    setFormState(updatedState);
    syncParentState(updatedState);
    setHasUnsavedChanges(true);
  };

  const handleTextChange =
    (field: keyof IEmailCustomizationFormState) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      updateField(field, value);
    };

  const handleMediaTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newMediaType = e.target.value as EventEmailCustomizationMediaType;
    const updatedState = {
      ...formState,
      mediaType: newMediaType,
    };

    if (newMediaType === EventEmailCustomizationMediaType.IMAGE) {
      updatedState.mediaExtraImageURL = "";
    }

    setFormState(updatedState);
    syncParentState(updatedState);
    setHasUnsavedChanges(true);
  };

  const handleImageUpload =
    (field: keyof IEmailCustomizationFormState) => (url: string) => {
      const currentState = formStateRef.current;
      const updatedState = {
        ...currentState,
        [field]: url,
      };

      if (
        field === "headerImageURL" ||
        field === "mediaURL" ||
        field === "mediaExtraImageURL" ||
        field === "senderImageURL"
      ) {
        const error = validateURLField(field as string, url);
        setUrlErrors((prev) => ({
          ...prev,
          [field]: error || "",
        }));
      }

      setFormState(updatedState);
      syncParentState(updatedState);
      setHasUnsavedChanges(true);
    };

  const handleImageDelete =
    (field: keyof IEmailCustomizationFormState) => () => {
      const currentState = formStateRef.current;
      const updatedState = JSON.parse(JSON.stringify(currentState));
      updatedState[field] = "";

      setUrlErrors((prev) => ({
        ...prev,
        [field]: "",
      }));

      setFormState(updatedState);
      syncParentState(updatedState);
      setHasUnsavedChanges(true);
    };

  const handleOpenPreview = () => {
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleClearForm = () => {
    setIsClearing(true);

    const clearedState: IEmailCustomizationFormState = {
      type: activeTab,
      mediaType: EventEmailCustomizationMediaType.VIDEO,
    };

    setFormState(clearedState);
    const activeIndex = customizations.findIndex((c) => c.type === activeTab);

    setUrlErrors({});

    if (activeIndex !== -1) {
      const updatedCustomizations = [...customizations];
      const currentCustomization = updatedCustomizations[activeIndex];

      updatedCustomizations[activeIndex] = {
        _id: currentCustomization._id,
        event: currentCustomization.event,
        ...clearedState,
      };

      onCustomizationChange(updatedCustomizations);
    } else {
      const newCustomization: IEmailCustomization = {
        event: "",
        ...clearedState,
      };
      onCustomizationChange([...customizations, newCustomization]);
    }

    setHasUnsavedChanges(true);
  };

  const activeCustomization = customizations.find(
    (customization) => customization.type === activeTab,
  );

  const hasCustomizationId = activeCustomization && activeCustomization._id;

  const hasAnyValue = Boolean(
    formState.subject ||
      formState.headerImageURL ||
      formState.textBlock1 ||
      formState.textBlock2 ||
      formState.textBlock3 ||
      formState.textBlock4 ||
      formState.ctaButtonText ||
      formState.mediaURL ||
      formState.mediaExtraImageURL ||
      formState.senderImageURL,
  );

  const validateAllUrlFields = () => {
    const errors: Record<string, string> = {};
    const urlFields: (keyof IEmailCustomizationFormState)[] = [
      "headerImageURL",
      "mediaURL",
      "mediaExtraImageURL",
      "senderImageURL",
    ];

    urlFields.forEach((field) => {
      const value = formState[field];
      if (typeof value === "string" && value) {
        const error = validateURLField(field as string, value);
        if (error) {
          errors[field] = error;
        }
      }
    });

    setUrlErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendTestEmail = async () => {
    if (!eventId) {
      return;
    }

    if (!validateAllUrlFields()) {
      return;
    }

    const formStateCopy = JSON.parse(JSON.stringify(formState));
    const dataToSend: IEmailCustomization = {
      ...formStateCopy,
      _id: activeCustomization?._id,
      event: eventId,
    };

    const cleanedData = removeEmptyStringFields(dataToSend);
    await sendTestEmail(cleanedData);
  };

  const confirmSave = async () => {
    const activeIndex = customizations.findIndex((c) => c.type === activeTab);

    if (activeIndex !== -1) {
      const updatedCustomizations = JSON.parse(JSON.stringify(customizations));
      const currentCustomization = updatedCustomizations[activeIndex];
      const formStateCopy = JSON.parse(JSON.stringify(formState));

      updatedCustomizations[activeIndex] = {
        ...currentCustomization,
        ...formStateCopy,
        type: activeTab,
      };

      onCustomizationChange(updatedCustomizations);
    }

    await onSave();
    setHasUnsavedChanges(false);
  };

  const handleSave = async () => {
    if (!validateAllUrlFields()) {
      return;
    }

    dispatch(
      addDialogNotification({
        message: t("eventForm:customize.emails.confirm.saving.note"),
        lightDialog: true,
        title: t("manageEvent:warning.title"),
        confirmText: t("common:dialog.button.confirm"),
        dismissText: t("common:button.cancel"),
        onConfirm: () => confirmSave(),
        onDismiss: () => {},
      }),
    );
  };

  const handleReset = () => {
    onReset();
    setHasUnsavedChanges(false);
  };

  const isLeavingChanges = (next: Location) => {
    if (hasUnsavedChanges) {
      dispatch(
        addDialogNotification({
          message: t("eventForm:customize.emails.unsaved.changes.note"),
          lightDialog: true,
          title: t("manageEvent:warning.title"),
          confirmText: t("manageEvent:confirm.text"),
          dismissText: t("manageEvent:dismiss.text"),
          onConfirm: () =>
            confirmSave()
              .then((res) => {
                setHasUnsavedChanges(false);
                setTimeout(() => {
                  history.push(next.pathname);
                }, 100);
                return res;
              })
              .catch((_err) => {
                setHasUnsavedChanges(false);
                setTimeout(() => {
                  history.push(next.pathname);
                }, 100);
              }),
          onDismiss: () => {
            setHasUnsavedChanges(false);
            setTimeout(() => {
              history.push(next.pathname);
            }, 100);
          },
        }),
      );
      return false;
    }
    return true;
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        <Info sx={styles.icon} />
        <div>
          <Typography variant="body1">
            {t("eventForm:customize.emails.note")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {t("eventForm:customize.emails.note.2")}
          </Typography>
          <Box sx={styles.buttons}>
            <Button
              onClick={handleOpenPreview}
              color="dark"
              size="sm"
              theme="light"
              variant="tertiary"
            >
              {t("event:preview")}
            </Button>
            <Button
              onClick={handleSendTestEmail}
              color="dark"
              size="sm"
              theme="light"
              variant="secondary"
              disabled={isSending || !eventId}
            >
              {t("eventForm:customize.emails.send.test.email")}
            </Button>
          </Box>
        </div>
      </Paper>

      <Box>
        <Box mb={2}>
          <Input
            value={
              customEmailSenderSettings?.email ||
              getVar("REACT_APP_DEFAULT_EMAIL_SENDER_ADDRESS")
            }
            disabled
            fullWidth
            size="sm"
            theme="light"
            labelComponent={() => (
              <Typography variant="h5" sx={styles.senderInputLabels}>
                {t("eventForm:customize.emails.sender.address.label")}
              </Typography>
            )}
          />
        </Box>

        <Box mb={2}>
          <Input
            value={
              customEmailSenderSettings?.fromName ||
              getVar("REACT_APP_DEFAULT_EMAIL_SENDER_NAME")
            }
            disabled
            fullWidth
            size="sm"
            theme="light"
            labelComponent={() => (
              <Typography variant="h5" sx={styles.senderInputLabels}>
                {t("eventForm:customize.emails.sender.name.label")}
              </Typography>
            )}
          />
        </Box>

        <Box mb={2}>
          <Input
            label={t("eventForm:customize.emails.subject.line.label")}
            value={formState.subject || ""}
            onChange={handleTextChange("subject")}
            fullWidth
            size="sm"
            theme="light"
            placeholder={t("eventForm:customize.emails.text.placeholder")}
            inputProps={{ maxLength: 70 }}
            getRemainingCharsMessage={(key) =>
              t("common:character.remaining", { key })
            }
          />
        </Box>

        <Box mb={2}>
          <ImageUploaderField
            label={t("eventForm:customize.emails.header.image.url.label")}
            name="headerImageURL"
            uploadedImage={formState.headerImageURL || ""}
            onImageUpload={handleImageUpload("headerImageURL")}
            onImageDelete={handleImageDelete("headerImageURL")}
            bestSize={BestUploadSizes.emailHeaderImage}
            dataTestId="header-image-uploader"
            error={urlErrors.headerImageURL}
          />
        </Box>

        <Box mb={2} display="flex">
          <FormTextInput
            label={t("eventForm:customize.emails.text.block.1.label")}
            value={formState.textBlock1 || ""}
            onChange={handleTextChange("textBlock1")}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            sx={styles.formTextInput}
            InputLabelProps={{
              shrink: true,
              sx: styles.textAreaLabel,
            }}
            placeholder={t("eventForm:customize.emails.text.placeholder")}
            inputProps={{ maxLength: 500 }}
            getRemainingCharsMessage={(key) =>
              t("common:character.remaining", { key })
            }
          />
        </Box>
      </Box>

      {activeTab === EventEmailCustomizationType.INVITE && (
        <Box>
          <Box mb={2}>
            <Input
              label={t(
                "eventForm:customize.emails.accept.invite.button.text.label",
              )}
              value={formState.ctaButtonText || ""}
              onChange={handleTextChange("ctaButtonText")}
              fullWidth
              size="sm"
              theme="light"
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 60 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <Box mb={2} display="flex">
            <FormTextInput
              label={t("eventForm:customize.emails.text.block.2.label")}
              value={formState.textBlock2 || ""}
              onChange={handleTextChange("textBlock2")}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={styles.formTextInput}
              InputLabelProps={{
                shrink: true,
                sx: styles.textAreaLabel,
              }}
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 500 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <MediaSection
            mediaType={
              formState.mediaType || EventEmailCustomizationMediaType.VIDEO
            }
            mediaURL={formState.mediaURL || ""}
            mediaExtraImageURL={formState.mediaExtraImageURL || ""}
            onMediaTypeChange={handleMediaTypeChange}
            onMediaURLChange={handleTextChange("mediaURL")}
            onMediaImageUpload={handleImageUpload("mediaURL")}
            onMediaImageDelete={handleImageDelete("mediaURL")}
            onMediaExtraImageUpload={handleImageUpload("mediaExtraImageURL")}
            onMediaExtraImageDelete={handleImageDelete("mediaExtraImageURL")}
            mediaURLError={urlErrors.mediaURL}
            mediaExtraImageURLError={urlErrors.mediaExtraImageURL}
          />
          <Box mb={2}>
            <ImageUploaderField
              name="senderImageURL"
              label={t("eventForm:customize.emails.sender.image.label")}
              uploadedImage={formState.senderImageURL || ""}
              onImageUpload={handleImageUpload("senderImageURL")}
              onImageDelete={handleImageDelete("senderImageURL")}
              bestSize={BestUploadSizes.senderImage}
              dataTestId="sender-image-uploader"
              error={urlErrors.senderImageURL}
            />
          </Box>
        </Box>
      )}

      {activeTab === EventEmailCustomizationType.REGISTRATION_CONFIRMATION && (
        <Box>
          <Box mb={2} display="flex">
            <FormTextInput
              label={t("eventForm:customize.emails.text.block.2.label")}
              value={formState.textBlock2 || ""}
              onChange={handleTextChange("textBlock2")}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={styles.formTextInput}
              InputLabelProps={{
                shrink: true,
                sx: styles.textAreaLabel,
              }}
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 500 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <Box mb={2}>
            <Input
              label={t(
                "eventForm:customize.emails.event.link.button.text.label",
              )}
              value={formState.ctaButtonText || ""}
              onChange={handleTextChange("ctaButtonText")}
              fullWidth
              size="sm"
              theme="light"
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 60 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <Box mb={2} display="flex">
            <FormTextInput
              label={t("eventForm:customize.emails.text.block.3.label")}
              value={formState.textBlock3 || ""}
              onChange={handleTextChange("textBlock3")}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={styles.formTextInput}
              InputLabelProps={{
                shrink: true,
                sx: styles.textAreaLabel,
              }}
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 500 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <Box mb={2} display="flex">
            <FormTextInput
              label={t("eventForm:customize.emails.text.block.4.label")}
              value={formState.textBlock4 || ""}
              onChange={handleTextChange("textBlock4")}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={styles.formTextInput}
              InputLabelProps={{
                shrink: true,
                sx: styles.textAreaLabel,
              }}
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 500 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <MediaSection
            mediaType={
              formState.mediaType || EventEmailCustomizationMediaType.VIDEO
            }
            mediaURL={formState.mediaURL || ""}
            mediaExtraImageURL={formState.mediaExtraImageURL || ""}
            onMediaTypeChange={handleMediaTypeChange}
            onMediaURLChange={handleTextChange("mediaURL")}
            onMediaImageUpload={handleImageUpload("mediaURL")}
            onMediaImageDelete={handleImageDelete("mediaURL")}
            onMediaExtraImageUpload={handleImageUpload("mediaExtraImageURL")}
            onMediaExtraImageDelete={handleImageDelete("mediaExtraImageURL")}
            mediaURLError={urlErrors.mediaURL}
            mediaExtraImageURLError={urlErrors.mediaExtraImageURL}
          />
          <Box mb={2}>
            <ImageUploaderField
              name="senderImageURL"
              label={t("eventForm:customize.emails.sender.image.label")}
              uploadedImage={formState.senderImageURL || ""}
              onImageUpload={handleImageUpload("senderImageURL")}
              onImageDelete={handleImageDelete("senderImageURL")}
              bestSize={BestUploadSizes.senderImage}
              dataTestId="sender-image-uploader"
              error={urlErrors.senderImageURL}
            />
          </Box>
        </Box>
      )}

      {(activeTab === EventEmailCustomizationType.DAYS_REMINDER ||
        activeTab === EventEmailCustomizationType.MINUTES_REMINDER) && (
        <Box>
          <Box mb={2}>
            <Input
              label={t(
                "eventForm:customize.emails.event.link.button.text.label",
              )}
              value={formState.ctaButtonText || ""}
              onChange={handleTextChange("ctaButtonText")}
              fullWidth
              size="sm"
              theme="light"
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 60 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <Box mb={2} display="flex">
            <FormTextInput
              label={t("eventForm:customize.emails.text.block.2.label")}
              value={formState.textBlock2 || ""}
              onChange={handleTextChange("textBlock2")}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={styles.formTextInput}
              InputLabelProps={{
                shrink: true,
                sx: styles.textAreaLabel,
              }}
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 500 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>
          <MediaSection
            mediaType={
              formState.mediaType || EventEmailCustomizationMediaType.VIDEO
            }
            mediaURL={formState.mediaURL || ""}
            mediaExtraImageURL={formState.mediaExtraImageURL || ""}
            onMediaTypeChange={handleMediaTypeChange}
            onMediaURLChange={handleTextChange("mediaURL")}
            onMediaImageUpload={handleImageUpload("mediaURL")}
            onMediaImageDelete={handleImageDelete("mediaURL")}
            onMediaExtraImageUpload={handleImageUpload("mediaExtraImageURL")}
            onMediaExtraImageDelete={handleImageDelete("mediaExtraImageURL")}
            mediaURLError={urlErrors.mediaURL}
            mediaExtraImageURLError={urlErrors.mediaExtraImageURL}
          />
          <Box mb={2} display="flex">
            <FormTextInput
              label={t("eventForm:customize.emails.text.block.3.label")}
              value={formState.textBlock3 || ""}
              onChange={handleTextChange("textBlock3")}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={styles.formTextInput}
              InputLabelProps={{
                shrink: true,
                sx: styles.textAreaLabel,
              }}
              placeholder={t("eventForm:customize.emails.text.placeholder")}
              inputProps={{ maxLength: 500 }}
              getRemainingCharsMessage={(key) =>
                t("common:character.remaining", { key })
              }
            />
          </Box>

          <Box mb={2}>
            <ImageUploaderField
              name="senderImageURL"
              label={t("eventForm:customize.emails.sender.image.label")}
              uploadedImage={formState.senderImageURL || ""}
              onImageUpload={handleImageUpload("senderImageURL")}
              onImageDelete={handleImageDelete("senderImageURL")}
              bestSize={BestUploadSizes.senderImage}
              dataTestId="sender-image-uploader"
              error={urlErrors.senderImageURL}
            />
          </Box>
        </Box>
      )}

      <EmailPreviewDialog
        open={isPreviewOpen}
        onClose={handleClosePreview}
        activeTab={activeTab}
        customization={activeCustomization}
        senderEmail={
          customEmailSenderSettings?.email ||
          getVar("REACT_APP_DEFAULT_EMAIL_SENDER_ADDRESS")
        }
        senderName={
          customEmailSenderSettings?.fromName ||
          getVar("REACT_APP_DEFAULT_EMAIL_SENDER_NAME")
        }
      />

      <Box sx={styles.bottomButtons} mt={4}>
        <Button
          onClick={handleSave}
          color="dark"
          size="sm"
          theme="light"
          variant="secondary"
          disabled={
            isSaving || Object.keys(urlErrors).some((key) => urlErrors[key])
          }
        >
          {isSaving
            ? t("manageEvent:saving")
            : t("eventForm:customize.emails.save.changes")}
        </Button>
        <Button
          onClick={handleReset}
          color="dark"
          size="sm"
          theme="light"
          variant="tertiary"
          disabled={!hasCustomizationId}
        >
          {t("eventForm:customize.emails.undo.changes")}
        </Button>
        <Button
          onClick={handleClearForm}
          color="dark"
          size="sm"
          theme="light"
          variant="warning"
          disabled={!hasAnyValue}
        >
          {t("common:clear.form")}
        </Button>
      </Box>

      <Prompt message={isLeavingChanges} />
    </Box>
  );
};
